/**
 * Kiểu dữ liệu cấu hình loại chứng từ của module `ocr` (`/api/v1/m/ocr/schemas`, đợt 1–2).
 *
 * Chép đúng hợp đồng của `modules/ocr/backend/src/schema/`: tên trường giữ `snake_case`.
 * Khi module có OpenAPI và client sinh tự động thì xoá tệp này.
 */

export const DOCUMENT_TYPES = [
  'INVOICE',
  'RECEIPT',
  'CONTRACT',
  'STATEMENT',
  'MINUTES',
  'WAREHOUSE_RECEIPT',
  'OTHERS',
] as const;
export type DocumentType = (typeof DOCUMENT_TYPES)[number];

export const FIELD_DATA_TYPES = ['TEXT', 'DATE', 'NUMBER', 'CURRENCY', 'BOOLEAN', 'LIST'] as const;
export type FieldDataType = (typeof FIELD_DATA_TYPES)[number];

export const FIELD_POSITIONS = ['HEADER', 'BODY', 'FOOTER'] as const;
export type FieldPosition = (typeof FIELD_POSITIONS)[number];

/** Cột lọc nhanh của `ocr_document` mà một trường đổ giá trị vào. */
export const DENORM_TARGETS = [
  'INVOICE_NUMBER',
  'ISSUE_DATE',
  'SELLER_TAX_CODE',
  'SELLER_NAME',
  'TOTAL_AMOUNT',
  'VAT_AMOUNT',
  'GRAND_TOTAL',
] as const;
export type DenormTarget = (typeof DENORM_TARGETS)[number];

export const EXTRACTION_MODES = ['SINGLE_MODEL', 'OCR_THEN_MAP'] as const;
export type ExtractionMode = (typeof EXTRACTION_MODES)[number];

/** Các cột cấp loại chứng từ, chung cho danh sách và chi tiết. */
export interface OcrSchemaBase {
  id: string;
  code: string;
  name: string;
  type: DocumentType;
  /** Cũng là prompt của bước suy luận. */
  description: string | null;
  is_active: boolean;
  split_pdf_pages: boolean;
  enable_word_boxes: boolean;
  transform_by_prompt: boolean;
  model_self_map: boolean;
  /** `ocr_ai_model.key`; `null` = model mặc định của tenant. */
  ocr_provider: string | null;
  extraction_mode: ExtractionMode;
  /** Model đọc chữ ở chế độ `OCR_THEN_MAP`. */
  ocr_reader_provider: string | null;
  created_at: string;
  updated_at: string;
  created_by: string;
}

/** Một dòng của `GET /schemas`. */
export interface OcrSchemaListItem extends OcrSchemaBase {
  field_count: number;
  table_count: number;
  document_count: number;
}

export interface OcrSchemaPage {
  items: OcrSchemaListItem[];
  next_cursor: string | null;
}

export interface OcrField {
  id: string;
  field_key: string;
  label: string;
  data_type: FieldDataType;
  position: FieldPosition;
  is_required: boolean;
  is_unique: boolean;
  validation_regex: string | null;
  description: string | null;
  /** Nhãn in thật trên chứng từ, nhiều cách in ngăn bằng `|`. */
  source_label: string | null;
  display_order: number;
  denorm_target: DenormTarget | null;
  created_at: string;
  updated_at: string;
  created_by: string;
}

export interface OcrColumn {
  id: string;
  column_key: string;
  label: string;
  data_type: FieldDataType;
  is_required: boolean;
  description: string | null;
  source_label: string | null;
  display_order: number;
  created_at: string;
  updated_at: string;
  created_by: string;
}

export interface OcrTable {
  id: string;
  table_key: string;
  name: string;
  description: string | null;
  display_order: number;
  /** `false` = cột ghép theo thứ tự khai, không theo nhãn. */
  has_header_row: boolean;
  columns: OcrColumn[];
  created_at: string;
  updated_at: string;
  created_by: string;
}

/** `GET /schemas/{id}`, `GET /schemas/code/{code}`. */
export interface OcrSchemaDetail extends OcrSchemaBase {
  fields: OcrField[];
  tables: OcrTable[];
}

/** `GET /schemas/stats`. */
export interface OcrSchemaStats {
  total_schemas: number;
  active_schemas: number;
  total_fields: number;
  total_tables: number;
}

/** Thân `PATCH /schemas/{id}`. */
export interface OcrSchemaPatch {
  name?: string;
  type?: DocumentType;
  description?: string | null;
  is_active?: boolean;
  ocr_provider?: string | null;
  extraction_mode?: ExtractionMode;
  ocr_reader_provider?: string | null;
  split_pdf_pages?: boolean;
  enable_word_boxes?: boolean;
  transform_by_prompt?: boolean;
  model_self_map?: boolean;
}

export interface OcrFieldInput {
  field_key: string;
  label: string;
  data_type: FieldDataType;
  position: FieldPosition;
  is_required?: boolean;
  is_unique?: boolean;
  validation_regex?: string | null;
  description?: string | null;
  source_label?: string | null;
  display_order?: number;
  denorm_target?: DenormTarget | null;
}

export interface OcrColumnInput {
  column_key: string;
  label: string;
  data_type: FieldDataType;
  is_required?: boolean;
  description?: string | null;
  source_label?: string | null;
  display_order?: number;
}

export interface OcrTableInput {
  table_key: string;
  name: string;
  description?: string | null;
  has_header_row?: boolean;
  /** Bắt buộc là mảng, kể cả rỗng. */
  columns: OcrColumnInput[];
}

/** Thân `POST /schemas`: tạo kèm trường, bảng, cột trong một transaction. */
export interface OcrSchemaCreate extends Omit<OcrSchemaPatch, 'is_active'> {
  code: string;
  name: string;
  type: DocumentType;
  fields: OcrFieldInput[];
  tables: OcrTableInput[];
}

export type OcrFieldPatch = Partial<OcrFieldInput>;
export type OcrTablePatch = Partial<Omit<OcrTableInput, 'columns'>>;
export type OcrColumnPatch = Partial<OcrColumnInput>;
